import React, { useState, useEffect, useMemo } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { ReactComponent as ExportIcon } from "assets/images/icons/TopNavIcons/ExportIcon.svg";
import { useForm, FormProvider } from "react-hook-form";
import { toast } from "react-toastify";
import useTableRequest from "hooks/useTableRequest";
import DataTable from "components/organisms/DataTable/DataTable";
import GlobalTableSearch from "components/organisms/DataTable/components/GlobalTableSearch";
import PillTabs from "components/atoms/PillTabs/PillTabs";
import Badge from "components/atoms/Badge/Badge";

import {
  getTeamMemberList,
  getTeamMemberListById,
  exportDetail,
  getInviteList,
  updateTeamMemberDetail,
  createInviteUser,
} from "api";

import Button from "components/atoms/Button/Button";
import usePermissions from "hooks/usePermissions";
import errorToast from "helpers/errorToast";
import ManageTeamMember from "./ManageTeamMember";
import ConfirmationModal from "./ConfirmationModal";
import "./commonUsers.scss";

const CommonUsers = function () {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const methods = useForm({ shouldFocusError: true, mode: "all" });
  const { handleSubmit, getValues, reset, setError, clearErrors } = methods;
  const { permissions, getPaymentLocationLabel } = usePermissions();

  const [downloading, setDownloading] = useState(false);
  const label = getPaymentLocationLabel();
  const [showManageDialog, setShowManageDialog] = useState(false);
  const [selectedEditTeamMember, setSelectedEditTeamMember] = useState({});
  const [category, setCategory] = useState("active,pending");
  const [showDeleteMessageDialog, setShowDeleteMessageDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState({});
  const [inviteOptions, setInvitedOptions] = useState([]);
  const [isUpdateData, setIsUpdateData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const adminSettings = JSON.parse(permissions || "{}")?.permissions?.admin_settings;

  useEffect(() => {
    getInviteList()
      .then((res) => {
        setInvitedOptions(res.data);
      })
      .catch((error) => errorToast(error));
  }, []);

  const columns = useMemo(
    () => [
      {
        Header: (
          <span className="pl-9 ">
            <FormattedMessage id="general.name" defaultMessage="NAME" />
          </span>
        ),
        sortable: true,
        accessor: "name", // accessor is the "key" in the data
        Cell: ({ value }) => <p className="font-weight-bold pl-9 pt-8 pb-8 mb-0">{value}</p>,
      },
      {
        Header: <span className="">{label}</span>,
        accessor: "payment_location",
        sortable: true,
        maxWidth: 300,
        Cell: ({ value }) => {
          let location = value?.split(",");
          if (location && location?.length > 3) {
            const firstThreeLocations = location.slice(0, 3);
            const restLocations = location.slice(3);
            location = [...firstThreeLocations, `+${restLocations.length} others`];
          }

          return (
            <div style={{ width: "100%", overflow: "hidden" }}>
              <p className="pt-8 pb-8 mb-0">{location?.join(", ").replace(/(^,)|(,$)/g, "") || "-"}</p>
            </div>
          );
        },
      },
      {
        Header: (
          <span className="mb-0">
            <FormattedMessage id="settings.team-members.tableRoleHeader" defaultMessage="Role" />
          </span>
        ),
        sortable: true,
        accessor: "role",
        Cell: ({ value }) => <div className="pt-8 pb-8">{value}</div>,
      },
      {
        Header: (
          <span className="mb-0 text-right ">
            <FormattedMessage id="general.status" defaultMessage="Status" />
          </span>
        ),
        accessor: "status",
        sortable: true,
        Cell: ({ value }) => {
          const badgeType = (status) => {
            if (status === "Active") return "primary";
            if (status === "Pending") return "warning";
            return "secondary";
          };
          return (
            <div className="d-flex">
              <Badge type={badgeType(value)} label={value} />
            </div>
          );
        },
      },
      {
        Header: (
          <span className="mb-0">
            <FormattedMessage id="settings.team-members.supplierCompanyCode" defaultMessage="Supplier Company Code" />
          </span>
        ),
        accessor: "supplier_company_slug",
        Cell: ({ value }) => <div className="pt-8 pb-8">{value || ""}</div>,
      },
    ],
    [],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const openAddTeamMemberDialog = () => {
    setSelectedEditTeamMember({});
    setShowManageDialog(true);
    setIsUpdateData(false);
  };

  const openEditTeamMemberDialog = () => {
    setShowManageDialog(true);
    setIsUpdateData(false);
  };

  const closeEditTeamMemberDialog = () => {
    setShowManageDialog(false);
    setSelectedEditTeamMember({});
    clearErrors();
    reset();
  };

  function handleClick(value) {
    setSelectedEditTeamMember({});
    getTeamMemberListById(value.email)
      .then((res) => {
        if (res.data) {
          setSelectedEditTeamMember({ ...res.data, status: value.status });
        }
      })
      .catch((err) =>
        errorToast(
          err,
          intl.formatMessage({
            id: "settings.team-members.couldNotRetrieveTeamMemberDetail",
            defaultMessage: "Could not retrieve team member details. Please try again later.",
          }),
        ),
      );
  }

  const handleDownload = () => {
    setDownloading(true);
    const config = { responseType: "blob" };
    exportDetail(config).then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `${Date.now()}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setDownloading(false);
    });
  };

  const { instance, fetching, totalData, refetch } = useTableRequest(getTeamMemberList, columns, {
    params: {},
    filters: (globalFilter) => ({
      ...(globalFilter.query?.length && { query: globalFilter.query }),
      status: category,
    }),
    deps: [category],
  });

  const onSubmit = (data) => {
    if (!adminSettings?.edit_users) return;
    setIsLoading(true);
    if (data.approval_level === "") delete data.approval_level;
    if (!getValues("selected_locations")?.length) {
      setError("selected_locations", {
        types: {
          required: "required",
        },
      });
    }
    if (getValues("selected_locations")?.length) {
      const url = selectedEditTeamMember?.email
        ? updateTeamMemberDetail(selectedEditTeamMember.email, data)
        : createInviteUser(data);
      url
        .then(() => {
          toast(
            intl.formatMessage({
              id: `${
                selectedEditTeamMember?.email
                  ? "settings.updateTeamMemberSuccessMessage"
                  : "settings.addTeamMemberSuccessMessage"
              }`,
              defaultMessage: `${
                selectedEditTeamMember?.email
                  ? "User account detail updated successfully"
                  : "User account created successfully"
              }`,
            }),
            { type: "success" },
          );
          reset();
          setShowManageDialog(false);
          setIsUpdateData(true);
        })
        .catch((error) => {
          errorToast(error);
          setShowManageDialog(true);
        })
        .finally(() => setIsLoading(false));
    }
  };

  useEffect(() => {
    if (isUpdateData) {
      setTimeout(() => {
        refetch();
      }, 1000);
    }
  }, [isUpdateData]);

  return (
    <>
      <ConfirmationModal
        isShowing={showDeleteMessageDialog}
        close={() => {
          setShowDeleteMessageDialog(false);
        }}
        dialogMessage={dialogMessage}
        setIsUpdateData={setIsUpdateData}
      />
      <FormProvider {...methods}>
        <ManageTeamMember
          selectedEditTeamMember={selectedEditTeamMember}
          showManageDialog={showManageDialog}
          closeEditTeamMemberDialog={closeEditTeamMemberDialog}
          setShowDeleteMessageDialog={setShowDeleteMessageDialog}
          setDialogMessage={setDialogMessage}
          inviteOptions={inviteOptions}
          handleSubmit={handleSubmit}
          onSubmit={onSubmit}
          isLoading={isLoading}
        />
      </FormProvider>

      <DataTable
        tableInstance={instance}
        totalData={totalData}
        isLoading={fetching}
        draggable={false}
        onRowClick={(row) => {
          handleClick(row.original);
          openEditTeamMemberDialog();
        }}
        headerLeft={
          <div className="d-md-flex justify-content-between flex-md-row flex-column w-100">
            <div className="d-flex  align-items-center mt-3 ">
              <PillTabs
                id="quote-statuses"
                activeKey={category}
                onSelect={(eventKey) => {
                  searchParams.set("page", "1");
                  setSearchParams(searchParams);
                  setCategory(eventKey);
                }}
                tabItems={[
                  {
                    eventKey: "active,pending",
                    title: intl.formatMessage({
                      id: "settings.team-members.activePendingFilter",
                      defaultMessage: "Active/Pending",
                    }),
                  },
                  {
                    eventKey: "inactive",
                    title: intl.formatMessage({ id: "settings.team-members.inactiveFilter", defaultMessage: "Inactive" }),
                  },
                  {
                    eventKey: "",
                    title: intl.formatMessage({
                      id: "settings.team-members.allFilter",
                      defaultMessage: "All accounts",
                    }),
                  },
                ]}
              />
              <div className="ml-3 ml-md-0">
                <GlobalTableSearch
                  currentFilters={instance.state.globalFilter}
                  setFilters={instance.setGlobalFilter}
                  filterKey="query"
                  eventContext="Team members"
                />
              </div>
            </div>

            <div className="d-flex align-items-center mt-3">
              {!downloading ? (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip>
                      <FormattedMessage
                        id="settings.team-members.exportTeamMemberOverview"
                        defaultMessage="Export team member overview"
                      />
                    </Tooltip>
                  }
                >
                  <ExportIcon onClick={handleDownload} className="cursor-pointer" />
                </OverlayTrigger>
              ) : (
                <div className="spinner-border spinner-border-sm" role="status" />
              )}
              {adminSettings?.edit_users && (
                <Button
                  label={intl.formatMessage({
                    id: "settings.team-members.addMember",
                    defaultMessage: "Add member",
                  })}
                  className="rounded-pill ml-3"
                  style={{ height: "26px" }}
                  onClick={openAddTeamMemberDialog}
                />
              )}
            </div>
          </div>
        }
      />
    </>
  );
};

CommonUsers.propTypes = {
  globalFilter: PropTypes.string,
  setGlobalFilter: PropTypes.func,
  value: PropTypes.string,
  columns: PropTypes.instanceOf(Array).isRequired,
};

CommonUsers.defaultProps = {
  globalFilter: "",
  setGlobalFilter: null,
  value: "",
};

export default CommonUsers;
